'use client';
import React, { useState, useCallback, useEffect } from 'react';

import dynamic from 'next/dynamic';

import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import { SearchViewModel } from '@api/v3';
import {
  DateType,
  formatMomentDate,
  getCurrentDate,
  getNMonthsLater,
  isInclusivelyAfterDay,
  URL_DATE_FORMAT,
} from '@utils/dateUtil';
import { Guest } from '@components/ui/guest-box/guest-box-tab/GuestBox';
import { InitialFormValues } from '@components/views/types';
import { useDevice } from '@contexts/device';
import Typography from '@components/Typography';

import {
  Content,
  Footer,
  StyledSimpleButton,
  StyledButton,
  DynamicRow,
  InputWrapper,
} from './styled';

const Modal = dynamic(() => import('@components/Modal/next/Modal'));
const SearchBarDateRangeInput = dynamic(
  () =>
    import(
      '@components/ui/search/SearchBarInput/SearchBarDateInput/SearchBarRangeDatePickerInput/SearchBarDateRangePickerInput'
    )
);
const DestinationSearch = dynamic(
  () => import('./DestinationSearch/DestinationSearch')
);
const SearchBarGuestBox = dynamic(
  () => import('@components/ui/guest-box/guest-box-tab/SearchBarGuestBox')
);
const ResponsiveSearch = dynamic(
  () =>
    import(
      '@components/views/hotel/components/SearchBar/components/ResponsiveSearch'
    )
);

interface HotelSearchFormProps {
  onCancel?(): void;
  onSubmit?(): void;
  initialValues?: InitialFormValues;
  setShowLoader?: (show: boolean) => void;
  isHoliday?: boolean;
}

const HotelSearchForm = ({
  onCancel,
  initialValues,
  setShowLoader,
  isHoliday,
}: HotelSearchFormProps) => {
  const [destination, setDestination] = useState<SearchViewModel>();
  const [destinationSearchText, setDestinationSearchText] = useState('');
  const [localStartDate, setLocalStartDate] = useState<DateType | null>(
    initialValues?.startDate || null
  );
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(
    initialValues?.endDate || null
  );

  const [guests, setGuests] = useState<Guest[]>([
    { id: 0, adultNumber: initialValues?.guestInfo?.numberOfAdultGuests || 2 },
  ]);
  const [errors, setErrors] = useState({
    destination: false,
    dates: false,
    guests: false,
  });

  const { isDesktop } = useDevice();

  const setDates = ({
    startDate,
    endDate,
  }: {
    startDate: DateType | null;
    endDate: DateType | null;
  }) => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    setErrors({ ...errors, dates: false });
  };

  const roomURL = useCallback(() => {
    const rooms = guests?.map((guest) => {
      if (!guest.childList || guest.childList.length === 0) {
        return `${guest.adultNumber}`; // Çocuk yoksa sadece yetişkin sayısını döndür
      }

      // Çocuk varsa, her zaman `_` koy ve tarihleri ekle
      const children = guest.childList
        .map((child) => formatMomentDate(child.date, URL_DATE_FORMAT))
        .join('_');

      return `${guest.adultNumber}_${children}`;
    });

    return `room=${rooms.join(',')}`;
  }, [guests]);

  const url =
    localStartDate && localEndDate
      ? `${destination?.url}?in=${formatMomentDate(
          localStartDate,
          URL_DATE_FORMAT
        )}&out=${formatMomentDate(localEndDate, URL_DATE_FORMAT)}&${roomURL()}`
      : `${destination?.url}?${roomURL()}`;
  const [childError, setChildCountError] = useState(false);
  const [clickEvetControl, setClickEvetControl] = useState(false);
  const handleNavigate = () => {
    setClickEvetControl(true);
    if (!destination) {
      setErrors({ ...errors, destination: true });
    } else if (!guests.length) {
      setErrors({ ...errors, guests: true });
    } else if (!childError) {
      window.open(`/${url}`, '_self');
    }

    if (destinationSearchText && !childError) setShowLoader?.(true);
  };

  const handleDestinationSearchTextChange = (value: string) =>
    setDestinationSearchText(value);

  const handleDestinationChange = (destination: SearchViewModel) => {
    setDestination(destination);
    setErrors({ ...errors, destination: false });
  };

  const handleGuestBoxSubmit = (guests: Guest[]) => {
    setGuests(guests);
    setErrors({ ...errors, guests: false });
  };

  useEffect(() => {
    if (initialValues) {
      setDates({
        startDate: initialValues.startDate || null,
        endDate: initialValues.endDate || null,
      });
      setGuests([
        {
          id: 0,
          adultNumber: initialValues.guestInfo?.numberOfAdultGuests || 2,
        },
      ]);
    }
  }, []);

  return isDesktop ? (
    <Modal
      onCancel={onCancel}
      title={
        <Typography variant='Title4' weight='SemiBold' color='D400'>
          Otel Ekleyin
        </Typography>
      }
      backgroundColor='lynxWhite'
      width={774}
      padding='44px'
      zIndex={98}
      borderRadius={8}
    >
      <Content>
        <DynamicRow>
          <InputWrapper>
            <LocationAnimationErrorBox
              borderRadius='8px'
              hasError={errors.destination}
            >
              <DestinationSearch
                placeholder='Otel Adı Veya Konum'
                onDestinationChange={handleDestinationChange}
                searchText={destinationSearchText}
                onSearchTextChange={handleDestinationSearchTextChange}
              />
            </LocationAnimationErrorBox>
          </InputWrapper>
          <InputWrapper>
            <LocationAnimationErrorBox
              borderRadius='8px'
              hasError={errors.dates}
            >
              <SearchBarDateRangeInput
                label='Ne Kadar Kalacaksınız?'
                placeholder='Giriş - Çıkış Tarihleri'
                isHoliday={isHoliday}
                dateProps={{
                  startDate: localStartDate,
                  endDate: localEndDate,
                  onDatesChange: setDates,
                  isOutsideRange: (day) =>
                    !isInclusivelyAfterDay(day, getCurrentDate()) ||
                    (localStartDate
                      ? !localEndDate
                        ? isInclusivelyAfterDay(
                            day,
                            getNMonthsLater(localStartDate, 1)
                          )
                        : false
                      : false),
                }}
                popoverProps={{
                  align: 'end',
                }}
              />
            </LocationAnimationErrorBox>
          </InputWrapper>
        </DynamicRow>
        <DynamicRow>
          <InputWrapper>
            <LocationAnimationErrorBox hasError={errors.guests}>
              <SearchBarGuestBox
                previousGuests={guests}
                onSubmit={(form) => handleGuestBoxSubmit(form)}
                setChildCountError={setChildCountError}
                setClickEvetControl={setClickEvetControl}
                clickEvetControl={clickEvetControl}
              />
            </LocationAnimationErrorBox>
          </InputWrapper>
        </DynamicRow>
      </Content>
      <Footer>
        <StyledSimpleButton onClick={onCancel}>Vazgeç</StyledSimpleButton>

        <StyledButton onClick={handleNavigate} variant='Primary'>
          Listele
        </StyledButton>
      </Footer>
    </Modal>
  ) : (
    <ResponsiveSearch
      title={
        <Typography variant='Title4' weight='SemiBold' color='D400'>
          Otel Ekleyin
        </Typography>
      }
      isHoliday={isHoliday}
      setShowLoader={setShowLoader}
      isShowModal={true}
      hideModal={() => onCancel?.()}
      drawerHeight='100%'
    />
  );
};

export default HotelSearchForm;
